import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Send, FileSignature, ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";

export default function Overview() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [company, setCompany] = useState(null);
  const [requests, setRequests] = useState([]);
  const [contracts, setContracts] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/company").then((r) => setCompany(r.data)).catch(() => {}),
      api.get("/requests").then((r) => setRequests(r.data)).catch(() => {}),
      api.get("/contracts").then((r) => setContracts(r.data)).catch(() => {}),
    ]);
  }, []);

  const stats = [
    { label: t("overview.statProfile"), value: company?.description ? t("overview.complete") : t("overview.toGenerate"), icon: Building2, to: "/dashboard/profile" },
    { label: t("overview.statRequests"), value: requests.length, icon: Send, to: "/dashboard/requests" },
    { label: t("overview.statContracts"), value: contracts.length, icon: FileSignature, to: "/dashboard/contracts" },
  ];

  return (
    <div className="p-8 lg:p-12 max-w-6xl" data-testid="overview-page">
      <div className="mb-8">
        <p className="overline text-gold">{t("overview.welcome", { name: user?.name?.split(" ")[0] || "" })}</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-midnight mt-2 tracking-tight">{t("overview.title")}</h1>
        <p className="text-muted-foreground mt-2">{t("overview.subtitle")}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-6">
        {stats.map((s, i) => (
          <Link key={i} to={s.to} data-testid={`overview-stat-${i}`}
            className="relative overflow-hidden border border-sand bg-white p-6 hover:-translate-y-1 hover:shadow-[0_16px_44px_rgba(11,19,43,0.10)] transition-all duration-300 group" style={{ borderTop: `3px solid ${["#D4AF37", "#2A9D8F", "#E76F51"][i]}` }}>
            <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full blur-2xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ background: ["#D4AF37", "#2A9D8F", "#E76F51"][i] }} />
            <div className="relative flex items-center justify-between">
              <s.icon className="h-6 w-6" strokeWidth={1.5} style={{ color: ["#D4AF37", "#2A9D8F", "#E76F51"][i] }} />
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-midnight transition-colors rtl:rotate-180" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-muted-foreground mt-6">{s.label}</p>
            <p className="font-serif text-2xl text-midnight mt-1">{s.value}</p>
          </Link>
        ))}
      </div>

      <div className="mt-10 border border-sand bg-midnight text-bone p-8 relative overflow-hidden">
        <div className="relative z-10 max-w-xl">
          <Sparkles className="h-7 w-7 text-gold" strokeWidth={1.5} />
          <h2 className="font-serif text-2xl mt-4">{t("overview.aiTitle")}</h2>
          <p className="text-bone/70 text-sm mt-2 leading-relaxed">{t("overview.aiText")}</p>
          <Link to="/dashboard/profile" data-testid="overview-cta-profile"
            className="inline-flex items-center gap-2 mt-6 bg-gold text-midnight px-6 py-3 text-sm font-medium hover:brightness-105 transition-all duration-300">
            {t("overview.aiCta")} <ArrowRight className="h-4 w-4 rtl:rotate-180" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}
